/**
 * AgentAvatar — Speaks a question aloud using ElevenLabs + optional D-ID lip-sync.
 *
 * mode="audio"  → ElevenLabs audio only (fast, no video)
 * mode="avatar" → ElevenLabs audio immediately + D-ID video in parallel
 *                 If D-ID arrives before audio ends: swap to lip-sync video
 *                 If audio ends first (likely): fire onDone, ignore late D-ID
 *
 * Always fires onDone when speaking finishes (or manual button click in parent).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const AGENT_COLORS = {
  chanakya: "#EF9F27",
  ashoka:   "#7F77DD",
  kalpana:  "#1D9E75",
  bhawna:   "#E63946",
};

const AGENT_INITIALS = {
  chanakya: "CH",
  ashoka:   "AS",
  kalpana:  "KA",
  bhawna:   "BH",
};

// Local paths — images in frontend/public/ (work both on localhost and production)
const LOCAL_AVATARS = {
  chanakya: "/chankya.png",
  ashoka:   "/ashoka.png",
  kalpana:  "/kalpana.png",
  bhawna:   "/bhawna.png",
};

function playBrowserTTS(text, language, onEnd) {
  if (!window.speechSynthesis) { onEnd?.(); return; }
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = language === "hi" ? "hi-IN" : "en-IN";
  utt.rate  = 0.88;
  utt.onend   = () => onEnd?.();
  utt.onerror = () => onEnd?.();
  window.speechSynthesis.speak(utt);
}

export default function AgentAvatar({
  agent    = "chanakya",
  topic    = "",
  language = "en",
  gender   = "",        // "male" | "female" | "" (falls back to agent default)
  text     = "",
  avatarUrl = "",
  mode     = "audio",   // "audio" | "avatar"
  onDone,
}) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const cancelledRef = useRef(false);  // prevents late callbacks after unmount / text change

  const [phase, setPhase]       = useState("idle");    // idle | loading | speaking | done
  const [videoUrl, setVideoUrl] = useState(null);

  const color    = AGENT_COLORS[agent]   || "#E63946";
  const initials = AGENT_INITIALS[agent] || agent.slice(0, 2).toUpperCase();
  // Always resolve avatar: prop → local file → initials
  const resolvedAvatarUrl = avatarUrl || LOCAL_AVATARS[agent] || "";
  const [imgSrc, setImgSrc] = useState(resolvedAvatarUrl);

  // Sync when avatarUrl / agent changes
  useEffect(() => {
    setImgSrc(avatarUrl || LOCAL_AVATARS[agent] || "");
  }, [avatarUrl, agent]);

  useEffect(() => {
    if (!text) return;

    // Cancel any in-progress speak from previous render
    cancelledRef.current = true;
    audioRef.current?.pause();
    window.speechSynthesis?.cancel();

    const cancelled = { value: false };
    cancelledRef.current = false;

    setVideoUrl(null);
    setPhase("loading");

    const guard = (fn) => (...args) => { if (!cancelled.value) fn(...args); };

    const fireDone = guard(() => { setPhase("done"); onDone?.(); });

    if (mode === "avatar") {
      runAvatarMode(cancelled, fireDone);
    } else {
      runAudioMode(cancelled, fireDone);
    }

    return () => {
      cancelled.value = true;
      audioRef.current?.pause();
      window.speechSynthesis?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, agent, language, gender, mode]);

  // ── audio-only mode ──────────────────────────────────────────────────
  async function runAudioMode(cancelled, fireDone) {
    let buf;
    try { buf = await api.agentSpeak(agent, language, text, topic, gender); } catch { buf = null; }
    if (cancelled.value) return;

    if (buf && buf.byteLength > 0) {
      try {
        const blob = new Blob([buf], { type: "audio/mpeg" });
        const url  = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { URL.revokeObjectURL(url); fireDone(); };
        audio.onerror = () => { URL.revokeObjectURL(url); fireDone(); };
        await audio.play();
        if (!cancelled.value) setPhase("speaking");
        return;
      } catch { /* autoplay blocked → fall through */ }
    }

    // Fallback: browser TTS
    if (!cancelled.value) setPhase("speaking");
    playBrowserTTS(text, language, fireDone);
  }

  // ── avatar mode: audio immediately, D-ID in parallel ─────────────────
  async function runAvatarMode(cancelled, fireDone) {
    // Launch both requests in parallel
    const audioPromise  = api.agentSpeak(agent, language, text, topic).catch(() => null);
    const avatarPromise = api.agentAvatarSpeak(agent, language, text, topic).catch(() => null);

    // --- Play audio as soon as it arrives ---
    let audioEl = null;
    let audioDone = false;

    const buf = await audioPromise;
    if (cancelled.value) return;

    if (buf && buf.byteLength > 0) {
      try {
        const blob = new Blob([buf], { type: "audio/mpeg" });
        const url  = URL.createObjectURL(blob);
        audioEl = new Audio(url);
        audioRef.current = audioEl;

        audioEl.onended = () => {
          audioDone = true;
          URL.revokeObjectURL(url);
          // D-ID already took care of onDone, or fire it now
          if (!cancelled.value) fireDone();
        };
        audioEl.onerror = () => { audioDone = true; if (!cancelled.value) fireDone(); };

        audioEl.play().catch(() => {});
        if (!cancelled.value) setPhase("speaking");
      } catch {
        audioEl = null;
      }
    }

    if (!audioEl) {
      if (!cancelled.value) setPhase("speaking");
      playBrowserTTS(text, language, () => { audioDone = true; if (!cancelled.value) fireDone(); });
    }

    // --- Wait for D-ID video ---
    const result = await avatarPromise;
    if (cancelled.value) return;

    const vUrl = result?.video_url;

    if (vUrl && !audioDone) {
      // D-ID arrived while audio is still playing → switch to video
      audioEl?.pause();
      window.speechSynthesis?.cancel();

      // Detach audio onended so it doesn't double-fire onDone
      if (audioEl) { audioEl.onended = null; audioEl.onerror = null; }

      if (!cancelled.value) setVideoUrl(vUrl);
      // onDone fires in handleVideoEnd
    }
    // If audioDone is already true (audio finished first), D-ID arrived too late — ignore it.
  }

  const handleVideoEnd = () => {
    setPhase("done");
    onDone?.();
  };

  // Trigger video play when URL changes
  useEffect(() => {
    if (videoUrl && videoRef.current) {
      videoRef.current.src = videoUrl;
      videoRef.current.play().catch(() => {});
    }
  }, [videoUrl]);

  const isLoading  = phase === "loading";
  const isSpeaking = phase === "speaking";

  return (
    <div className="flex flex-col items-center gap-3 select-none">
      {/* ── D-ID lip-sync video ── */}
      {videoUrl ? (
        <video
          ref={videoRef}
          onEnded={handleVideoEnd}
          playsInline
          style={{
            width: 240,
            height: 240,
            borderRadius: "50%",
            objectFit: "cover",
            border: `3px solid ${color}`,
            boxShadow: `0 0 36px ${color}55`,
          }}
        />
      ) : (
        /* ── Static avatar image with pulse ring ── */
        <div
          style={{
            width: 200,
            height: 200,
            borderRadius: "50%",
            border: `3px solid ${color}`,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: avatarUrl ? "transparent" : `${color}18`,
            animation: isSpeaking
              ? "av-pulse 0.55s ease-in-out infinite"
              : isLoading
              ? "av-pulse 1.5s ease-in-out infinite"
              : "none",
            boxShadow: isSpeaking ? `0 0 32px ${color}55` : "none",
            transition: "box-shadow 0.3s",
          }}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={agent}
              onError={() => setImgSrc(LOCAL_AVATARS[agent] || "")}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{ fontSize: 40, fontWeight: 700, color, letterSpacing: 4 }}>
              {initials}
            </span>
          )}
        </div>
      )}

      {/* ── Status label ── */}
      <div className="text-center">
        <p className="font-display text-xl uppercase tracking-wide text-white">
          {agent.charAt(0).toUpperCase() + agent.slice(1)}
        </p>
        <p className="font-mono-kl text-[10px] uppercase tracking-widest mt-0.5"
          style={{ color: (isLoading || isSpeaking) ? color : "rgba(255,255,255,0.35)" }}>
          {isLoading ? "Preparing…" : isSpeaking ? "Speaking…" : "Ready"}
        </p>
      </div>

      <style>{`
        @keyframes av-pulse {
          0%, 100% { transform: scale(1);     opacity: 0.88; }
          50%       { transform: scale(1.06); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
